import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { Field, ErrorMessage } from 'formik';

import { Checkbox } from 'primereact/checkbox';
import { PickList } from 'primereact/picklist';

import { intlShape } from '../../../components/prop-types';

const dimensionTemplate = dimension => (
  <div className="p-clearfix">
    <div>{dimension.displayName}</div>
  </div>
);

const messages = defineMessages({
  available: { id: 'attrs.available', defaultMessage: 'Available' },
  selected: { id: 'attrs.selected', defaultMessage: 'Selected' },
});

function assesmentTemplate_itemTemplate(edge) {
  const {
    node: { id, title },
  } = edge;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <strong>{title}</strong>
      <span>{`#${id} `}</span>
    </div>
  );
}

function assessmentDimensions_itemTemplate(item) {
  const { id, displayName } = item;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <strong>{displayName}</strong>
      <span>{`#${id} `}</span>
    </div>
  );
}

const templateAlphabetSorting = (first, second) => ((
  first.node.title.toUpperCase() > second.node.title.toUpperCase())
  ? 1
  : -1);
const dimAlphabetSorting = (first, second) => ((
  first.displayName.toUpperCase() > second.displayName.toUpperCase())
  ? 1
  : -1);

function InterestsCheckbox({
  field, form: { touched, errors }, id, labelText, ...props
}) {
  const displayError = touched[field.name] && errors[field.name];

  return (
    <React.Fragment>
      <Checkbox
        id={id}
        {...field}
        {...props}
        checked={field.value}
        onChange={field.onChange}
        className={displayError ? 'p-error' : ''}
        style={{ marginRight: '.25em' }}
      />
      <label htmlFor={id}>{labelText}</label>
      <ErrorMessage name={field.name} component="div" />
    </React.Fragment>
  );
}

InterestsCheckbox.propTypes = {
  field: PropTypes.objectOf(PropTypes.any).isRequired,
  form: PropTypes.objectOf(PropTypes.any).isRequired,
  id: PropTypes.string.isRequired,
  labelText: PropTypes.string.isRequired,
};

function AssessmentStep(props) {
  const {
    isCurrentStep,
    templates: { edges },
    sections: { behaviorSection, cognitiveSection, interestsSection },
    intl: { formatMessage },
  } = props;
  const sortedTemplates = edges.slice().sort(templateAlphabetSorting);
  const [templatesSource, setTemplatesSource] = useState(sortedTemplates);
  const [templatesTarget, setTemplatesTarget] = useState([]);
  const sortedTemplateTargets = templatesTarget.slice().sort(templateAlphabetSorting);

  const sortedBehavior = behaviorSection.dimensions.slice().sort(dimAlphabetSorting);
  const [behaviorsSource, setBehaviorsSource] = useState(sortedBehavior);
  const [behaviorsTarget, setBehaviorsTarget] = useState([]);
  const sortedBehaviorTargets = behaviorsTarget.slice().sort(dimAlphabetSorting);

  const sortedDistortion = behaviorSection.distortionDimensions.slice().sort(dimAlphabetSorting);
  const [distortionSource, setDistortionSource] = useState(sortedDistortion);
  const [distortionTarget, setDistortionTarget] = useState([]);
  const sortedDistortionTargets = distortionTarget.slice().sort(dimAlphabetSorting);

  const sortedCognitive = cognitiveSection.dimensions.slice().sort(dimAlphabetSorting);
  const [cognitiveSource, setCognitiveSource] = useState(sortedCognitive);
  const [cognitiveTarget, setCognitiveTarget] = useState([]);
  const sortedCognitiveTargets = cognitiveTarget.slice().sort(dimAlphabetSorting);

  const sortedInterests = interestsSection.dimensions.slice().sort(dimAlphabetSorting);
  const [interestsSource, setInterestsSource] = useState(sortedInterests);
  const [interestsTarget, setInterestsTarget] = useState([]);

  if (!isCurrentStep) {
    return null;
  }

  return (
    <>
      <div className="p-col-12">
        <FormattedMessage
          id="accounts.add-client.assessment-templates.heading"
          defaultMessage="Assign Assessment Templates"
          tagName="h1"
        />
        <FormattedMessage
          id="accounts.add-client.assessment-templates.description"
          defaultMessage="Allow scheduling assessments using the selected templates."
          tagName="p"
        />
      </div>
      <div className="p-col-12">
        <Field name="assessmentTemplates">
          {({ field, form }) => {
            const onChange = (e) => {
              setTemplatesSource(e.source);
              setTemplatesTarget(e.target);
              // const ids = e.target.map(edge => edge.node.id);
              form.setFieldValue(field.name, e.target);
            };
            return (
              <PickList
                source={templatesSource}
                target={sortedTemplateTargets}
                itemTemplate={assesmentTemplate_itemTemplate}
                onChange={onChange}
                sourceHeader={formatMessage(messages.available)}
                targetHeader={formatMessage(messages.selected)}
                showSourceControls={false}
                showTargetControls={false}
                responsive
              />
            );
          }}
        </Field>
      </div>
      <div className="p-col-12">
        <FormattedMessage
          id="accounts.add-client.assessment-dimensions.heading"
          defaultMessage="Assign Assessment Dimensions"
          tagName="h1"
        />
        <FormattedMessage
          id="accounts.add-client.assessment-dimensions.description"
          defaultMessage="Allow Assessment Template creation using the selected Dimensions."
          tagName="p"
        />
      </div>
      <div className="p-col-12">
        <h2>{behaviorSection.displayName}</h2>

        <Field name="assessmentDimensions.behaviorDimensions">
          {({ field, form }) => {
            const onChange = (e) => {
              setBehaviorsSource(e.source);
              setBehaviorsTarget(e.target);
              // const ids = e.target.map(item => item.id);
              form.setFieldValue(field.name, e.target);
              setDistortionSource([]);
              setDistortionTarget(sortedDistortion);
              form.setFieldValue('assessmentDimensions.distortionDimensions', sortedDistortion);
            };
            return (
              <PickList
                source={behaviorsSource}
                target={sortedBehaviorTargets}
                itemTemplate={assessmentDimensions_itemTemplate}
                onChange={onChange}
                sourceHeader={formatMessage(messages.available)}
                targetHeader={formatMessage(messages.selected)}
                showSourceControls={false}
                showTargetControls={false}
                responsive
              />
            );
          }}
        </Field>
      </div>
      <div className="p-col-12">
        <FormattedMessage
          id="accounts.add-client.assessment-dimensions.distortion-title"
          defaultMessage="Distortion"
          tagName="h3"
        />
        <FormattedMessage
          id="accounts.add-client.assessment-dimensions.distortion-description"
          defaultMessage="Distortion is automatically selected, if required."
          tagName="p"
        />

        <Field name="assessmentDimensions.distortionDimensions">
          {({ field, form }) => {
            const onChange = (e) => {
              setDistortionSource(e.source);
              setDistortionTarget(e.target);
              // const ids = e.target.map(item => item.id);
              form.setFieldValue(field.name, e.target);
            };
            return (
              <PickList
                source={distortionSource}
                target={sortedDistortionTargets}
                itemTemplate={assessmentDimensions_itemTemplate}
                onChange={onChange}
                sourceHeader={formatMessage(messages.available)}
                targetHeader={formatMessage(messages.selected)}
                showSourceControls={false}
                showTargetControls={false}
                responsive
              />
            );
          }}
        </Field>
      </div>
      <div className="p-col-12">
        <h2>{cognitiveSection.displayName}</h2>

        <Field name="assessmentDimensions.cognitiveDimensions">
          {({ field, form }) => {
            const onChange = (e) => {
              setCognitiveSource(e.source);
              setCognitiveTarget(e.target);
              // const ids = e.target.map(item => item.id);
              form.setFieldValue(field.name, e.target);
            };
            return (
              <PickList
                source={cognitiveSource}
                target={sortedCognitiveTargets}
                itemTemplate={dimensionTemplate}
                onChange={onChange}
                sourceHeader={formatMessage(messages.available)}
                targetHeader={formatMessage(messages.selected)}
                showSourceControls={false}
                showTargetControls={false}
                responsive
              />
            );
          }}
        </Field>
      </div>
      <div className="p-col-12">
        <h2>{interestsSection.displayName}</h2>
        <Field name="assessmentDimensions.interestsDimensionsArePresent">
          {({ field, form }) => {
            const onChange = (e) => {
              setInterestsSource(e.source);
              setInterestsTarget(e.target);
              // const ids = e.target.map(item => item.id);
              form.setFieldValue(field.name, !(interestsTarget.length > 0));
            };
            return (
              <PickList
                source={interestsSource}
                target={interestsTarget}
                itemTemplate={dimensionTemplate}
                onChange={onChange}
                className="interest-section-picklist"
                sourceHeader={formatMessage(messages.available)}
                targetHeader={formatMessage(messages.selected)}
                showSourceControls={false}
                showTargetControls={false}
                responsive
              />
            );
          }}
        </Field>
      </div>
    </>
  );
}

AssessmentStep.propTypes = {
  intl: intlShape.isRequired,
  isCurrentStep: PropTypes.bool.isRequired,
  templates: PropTypes.objectOf(PropTypes.any).isRequired,
  sections: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default injectIntl(AssessmentStep);
